import { ArrowRight, TrendingUp, Users, BarChart3, Zap } from 'lucide-react';

const stats = [
  { icon: TrendingUp, value: '340%', label: 'Avg. ROAS', color: 'from-blue-500 to-cyan-400' },
  { icon: Users, value: '2,500+', label: 'Leads/Month', color: 'from-violet-500 to-blue-500' },
  { icon: BarChart3, value: '$47M+', label: 'Revenue Generated', color: 'from-cyan-400 to-emerald-400' },
  { icon: Zap, value: '68%', label: 'Lower CPL', color: 'from-amber-400 to-orange-500' },
];

export default function HeroSection() {
  const handleScroll = (e, href) => {
    e.preventDefault();
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative min-h-screen flex items-center bg-vertex-gradient overflow-hidden"
    >
      {/* Background Effects */}
      <div className="absolute inset-0 bg-vertex-radial" />
      <div className="absolute top-1/4 -left-32 w-96 h-96 bg-vertex-500/10 rounded-full blur-3xl animate-float" />
      <div className="absolute bottom-1/4 -right-32 w-96 h-96 bg-vertex-600/10 rounded-full blur-3xl animate-float delay-300" />
      
      {/* Grid Pattern */}
      <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'url("data:image/svg+xml,%3csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 32 32%27 width=%2732%27 height=%2732%27 fill=%27none%27 stroke=%27rgb(255 255 255 / 0.5)%27%3e%3cpath d=%27M0 .5H31.5V32%27/%3e%3c/svg%3e")' }} />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-20">
        <div className="text-center max-w-5xl mx-auto">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-vertex-500/10 border border-vertex-500/20 mb-8 animate-fade-in">
            <span className="w-2 h-2 rounded-full bg-vertex-400 animate-pulse" />
            <span className="text-vertex-300 text-sm font-medium tracking-wide">Performance Marketing That Actually Performs</span>
          </div>

          {/* Main Headline */}
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-display font-extrabold text-white leading-[1.1] tracking-tight mb-6 animate-fade-in-up">
            Stop Burning Budget.
            <br />
            <span className="gradient-text">Start Scaling Revenue.</span>
          </h1>

          {/* Subheadline */}
          <p className="text-lg sm:text-xl text-gray-400 max-w-2xl mx-auto mb-10 leading-relaxed animate-fade-in-up delay-200">
            We engineer high-converting paid media systems that transform your ad spend into predictable, scalable pipeline. No fluff. No vanity metrics. Just revenue.
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16 animate-fade-in-up delay-300">
            <a
              href="#cta"
              onClick={(e) => handleScroll(e, '#cta')}
              data-testid="hero-cta-primary"
              className="btn-primary text-base group"
            >
              Book Your Strategy Call
              <ArrowRight className="w-5 h-5 ml-2 transition-transform duration-300 group-hover:translate-x-1" />
            </a>
            <a
              href="#results"
              onClick={(e) => handleScroll(e, '#results')}
              data-testid="hero-cta-secondary"
              className="btn-outline border-white/20 text-white hover:bg-white/10 hover:border-white/30 text-base"
            >
              See Our Results
            </a>
          </div>

          {/* Floating Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
            {stats.map((stat, i) => (
              <div
                key={stat.label}
                className={`glass rounded-2xl p-5 card-hover animate-fade-in-up`}
                style={{ animationDelay: `${400 + i * 100}ms` }}
              >
                <div className={`inline-flex p-2.5 rounded-xl bg-gradient-to-br ${stat.color} mb-3`}>
                  <stat.icon className="w-5 h-5 text-white" />
                </div>
                <div className="text-2xl sm:text-3xl font-display font-bold text-white mb-1">{stat.value}</div>
                <div className="text-gray-400 text-sm font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom Fade */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-white to-transparent" />
    </section>
  );
}
